import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, BookOpen, Clock, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelfPacedRouteOutline } from '@/hooks/useSelfPacedRouteOutline';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  programId: string;
  programName: string;
  description?: string | null;
}

/**
 * Modal informativo de una ruta de autoaprendizaje para usuarios sin acceso.
 * Cabecera fija con CTA "Suscribirme" + temario con scroll.
 */
export function SelfPacedRoutePreviewDialog({
  open,
  onOpenChange,
  programId,
  programName,
  description,
}: Props) {
  const navigate = useNavigate();
  const { data: chapters, isLoading } = useSelfPacedRouteOutline(programId, open);

  const totalContents = (chapters ?? []).reduce((acc, ch) => acc + ch.contents.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 max-h-[85vh] flex flex-col">
        {/* Cabecera fija */}
        <div className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur px-6 py-4 space-y-3 rounded-t-lg">
          <div className="space-y-1">
            <Badge variant="secondary" className="gap-1">
              <BookOpen className="w-3 h-3" />
              Ruta de autoaprendizaje
            </Badge>
            <h2 className="text-xl font-semibold leading-tight">{programName}</h2>
            {totalContents > 0 && (
              <p className="text-xs text-muted-foreground">
                {chapters?.length} capítulos · {totalContents} contenidos
              </p>
            )}
          </div>
          <Button
            className="w-full gap-2"
            onClick={e => {
              // El Dialog vive en un portal pero los eventos burbujean por el árbol
              // de React: sin esto, la tarjeta de la ruta también navega al detalle.
              e.stopPropagation();
              e.preventDefault();
              onOpenChange(false);
              navigate('/profile?tab=pagos');
            }}
          >
            <Sparkles className="w-4 h-4" />
            Suscribirme
          </Button>
        </div>

        {/* Contenido scrollable */}
        <div className="overflow-y-auto px-6 py-4 space-y-6">
          {description && (
            <p className="text-sm text-muted-foreground whitespace-pre-line">{description}</p>
          )}

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
            </div>
          ) : (chapters ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              El temario de esta ruta se publicará próximamente.
            </p>
          ) : (
            <div className="space-y-5">
              {chapters!.map((ch, idx) => (
                <div key={ch.id} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                      {idx + 1}
                    </span>
                    <h3 className="font-medium text-sm">{ch.name}</h3>
                  </div>
                  {ch.contents.length === 0 ? (
                    <p className="text-xs text-muted-foreground pl-8">Sin contenidos aún</p>
                  ) : (
                    <ul className="pl-8 space-y-1.5">
                      {ch.contents.map(c => (
                        <li
                          key={c.id}
                          className="flex items-start gap-2 text-sm text-muted-foreground"
                        >
                          <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-60" />
                          <span className="flex-1">{c.name}</span>
                          {c.content_type && (
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              {c.content_type}
                            </Badge>
                          )}
                          {c.duration && (
                            <span className="flex items-center gap-1 text-xs shrink-0">
                              <Clock className="w-3 h-3" />
                              {c.duration}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SelfPacedRoutePreviewDialog;
